// Route preloading service for optimizing chunk loading
export interface RoutePreloader {
  preloadRoute(routePath: string): Promise<void>;
  preloadCriticalRoutes(): Promise<void>;
  getPreloadPriority(routePath: string): 'high' | 'medium' | 'low';
}

// Route configuration type
interface RouteConfig {
  priority: 'high' | 'medium' | 'low';
  component: () => Promise<{ default: React.ComponentType }>;
  requiresAuth?: boolean;
}

// Route configuration with preload priorities
const ROUTE_CONFIG: Record<string, RouteConfig> = {
  // High priority - likely to be visited from landing page
  '/auth': { priority: 'high' as const, component: () => import('../pages/Auth') },
  '/dashboard': { priority: 'high' as const, component: () => import('../pages/Dashboard') },
  
  // Medium priority - common user flows
  '/profile': { priority: 'medium' as const, component: () => import('../pages/Profile') },
  '/my-folders': { priority: 'medium' as const, component: () => import('../pages/MyFolders') },
  '/legal': { priority: 'medium' as const, component: () => import('../pages/Legal') },
  '/outils': { priority: 'medium' as const, component: () => import('../pages/Outils') },
  '/experts': { priority: 'medium' as const, component: () => import('../pages/Experts') },
  
  // Low priority - specialized flows
  '/human-coach': { priority: 'low' as const, component: () => import('../pages/HumanCoach') },
  '/human-mediator': { priority: 'low' as const, component: () => import('../pages/HumanMediator') },
  '/human-arbitrator': { priority: 'low' as const, component: () => import('../pages/HumanArbitrator') },
  
  '/reset-password': { priority: 'low' as const, component: () => import('../pages/ResetPassword') },
  
  // Admin routes - only preload for admin users
  '/admin': { priority: 'low' as const, component: () => import('../pages/Admin'), requiresAuth: true },
  '/admin/users': { priority: 'low' as const, component: () => import('../pages/AdminUsers'), requiresAuth: true },
  '/admin/folders': { priority: 'low' as const, component: () => import('../pages/AdminFolders'), requiresAuth: true },
};

class RoutePreloaderService implements RoutePreloader {
  private preloadedRoutes = new Set<string>();
  private preloadPromises = new Map<string, Promise<void>>();

  getPreloadPriority(routePath: string): 'high' | 'medium' | 'low' {
    const config = ROUTE_CONFIG[routePath];
    return config?.priority || 'low';
  }

  async preloadRoute(routePath: string): Promise<void> {
    // Return existing promise if already preloading
    if (this.preloadPromises.has(routePath)) {
      return this.preloadPromises.get(routePath)!;
    }

    // Skip if already preloaded
    if (this.preloadedRoutes.has(routePath)) {
      return Promise.resolve();
    }

    const config = ROUTE_CONFIG[routePath];
    if (!config) {
      console.warn(`No preload configuration found for route: ${routePath}`);
      return Promise.resolve();
    }

    // Create preload promise
    const preloadPromise = this.performPreload(routePath, config);
    this.preloadPromises.set(routePath, preloadPromise);

    try {
      await preloadPromise;
      this.preloadedRoutes.add(routePath);
      console.log(`✅ Preloaded route: ${routePath}`);
    } catch (error) {
      console.error(`❌ Failed to preload route ${routePath}:`, error);
    } finally {
      this.preloadPromises.delete(routePath);
    }
  }

  private async performPreload(routePath: string, config: RouteConfig): Promise<void> {
    // Use requestIdleCallback for non-blocking preloading
    return new Promise((resolve, reject) => {
      const preloadFn = async () => {
        try {
          await config.component();
          resolve();
        } catch (error) {
          reject(error);
        }
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => preloadFn(), { timeout: 5000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(preloadFn, 100);
      }
    });
  }

  async preloadCriticalRoutes(): Promise<void> {
    const criticalRoutes = Object.entries(ROUTE_CONFIG)
      .filter(([_, config]) => config.priority === 'high')
      .map(([route]) => route);

    console.log('🚀 Preloading critical routes:', criticalRoutes);

    // Preload critical routes in parallel
    const preloadPromises = criticalRoutes.map(route => 
      this.preloadRoute(route).catch(error => {
        console.error(`Failed to preload critical route ${route}:`, error);
      })
    );

    await Promise.allSettled(preloadPromises);
  }

  // Preload routes based on user navigation patterns
  async preloadBasedOnCurrentRoute(currentRoute: string): Promise<void> {
    const preloadMap: Record<string, string[]> = {
      '/': ['/auth', '/dashboard', '/legal'], // From landing page
      '/auth': ['/dashboard', '/profile'], // After login
      '/dashboard': ['/my-folders', '/profile'], // From dashboard
      '/my-folders': ['/conversation/:conversationId'], // From folders
      '/legal': ['/outils', '/experts'], // Legal section navigation
    };

    const routesToPreload = preloadMap[currentRoute] || [];
    
    if (routesToPreload.length > 0) {
      console.log(`🎯 Preloading routes based on current route ${currentRoute}:`, routesToPreload);
      
      // Preload with delay to not interfere with current page loading
      setTimeout(() => {
        routesToPreload.forEach(route => {
          this.preloadRoute(route).catch(error => {
            console.error(`Failed to preload contextual route ${route}:`, error);
          });
        });
      }, 1000);
    }
  }

  // Check if user is likely to be an admin and preload admin routes
  async preloadAdminRoutesIfNeeded(userRole?: string): Promise<void> {
    if (userRole === 'admin' || userRole === 'super_admin') {
      const adminRoutes = ['/admin', '/admin/users', '/admin/folders'];
      console.log('👑 Preloading admin routes for admin user');
      
      adminRoutes.forEach(route => {
        this.preloadRoute(route).catch(error => {
          console.error(`Failed to preload admin route ${route}:`, error);
        });
      });
    }
  }

  // Get preload statistics for monitoring
  getPreloadStats() {
    return {
      preloadedCount: this.preloadedRoutes.size,
      preloadedRoutes: Array.from(this.preloadedRoutes),
      activePreloads: this.preloadPromises.size
    };
  }
}

// Export singleton instance
export const routePreloader = new RoutePreloaderService();

// Hook for using route preloader in components
export const useRoutePreloader = () => {
  return {
    preloadRoute: routePreloader.preloadRoute.bind(routePreloader),
    preloadCriticalRoutes: routePreloader.preloadCriticalRoutes.bind(routePreloader),
    preloadBasedOnCurrentRoute: routePreloader.preloadBasedOnCurrentRoute.bind(routePreloader),
    getPreloadStats: routePreloader.getPreloadStats.bind(routePreloader)
  };
};